<div class="container-fluid">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-1">
          <li class="breadcrumb-item"><a routerLink="/agremo-analyses">Agremo Analyses</a></li>
          <li class="breadcrumb-item active">Analysis Detail</li>
        </ol>
      </nav>
      <div class="d-flex align-items-center gap-3">
        <h1 class="h2 mb-0 fw-bold">
          {{ analysis?.analysisTypeName || 'Analysis' }}
        </h1>
        <span *ngIf="analysis" class="badge" [ngClass]="getStatusBadgeClass(analysis.status)">
          {{ getStatusLabel(analysis.status) }}
        </span>
      </div>
      <p class="text-muted mb-0 mt-1" *ngIf="analysis">
        {{ analysis.fieldName || 'Unknown Field' }} &middot; {{ formatDateTime(analysis.requestedAt) }}
      </p>
    </div>
    <div class="d-flex gap-2">
      <button
        *ngIf="canRecheckMap()"
        class="btn btn-outline-warning"
        [disabled]="recheckingMap"
        (click)="recheckMap()"
      >
        <span *ngIf="recheckingMap" class="spinner-border spinner-border-sm me-1"></span>
        <i *ngIf="!recheckingMap" class="bi bi-arrow-repeat me-1"></i>
        Recheck Map
      </button>
      <button
        *ngIf="canRetryDownload()"
        class="btn btn-outline-primary"
        [disabled]="retryingDownload"
        (click)="retryDownload()"
      >
        <span *ngIf="retryingDownload" class="spinner-border spinner-border-sm me-1"></span>
        <i *ngIf="!retryingDownload" class="bi bi-arrow-clockwise me-1"></i>
        Retry Download
      </button>
      <button class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-1"></i>
        Back
      </button>
    </div>
  </div>

  <!-- Success Message -->
  <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
    <i class="bi bi-check-circle-fill me-2"></i>
    {{ successMessage }}
    <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage && !loading" class="alert alert-danger alert-dismissible fade show" role="alert">
    <i class="bi bi-exclamation-triangle-fill me-2"></i>
    {{ errorMessage }}
    <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!loading && analysis" class="row">
    <!-- Left Column: Details -->
    <div class="col-lg-8">
      <!-- Analysis Info -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Analysis Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Organization</label>
              <p class="mb-0">{{ analysis.organizationName || analysis.organizationId }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Field</label>
              <p class="mb-0">{{ analysis.fieldName || 'N/A' }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Analysis Type</label>
              <p class="mb-0">{{ analysis.analysisTypeName || analysis.analysisTypeId || 'N/A' }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Credits Consumed</label>
              <p class="mb-0">{{ analysis.creditsConsumed }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Requested</label>
              <p class="mb-0">{{ formatDateTime(analysis.requestedAt) }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Completed</label>
              <p class="mb-0">{{ formatDateTime(analysis.completedAt) }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Map</label>
              <div class="d-flex align-items-center gap-2">
                <p class="mb-0">{{ analysis.mapName || 'N/A' }}</p>
                <span *ngIf="analysis.mapStatus" class="badge" [ngClass]="{
                  'bg-success': analysis.mapStatus === 'ready',
                  'bg-info text-dark progress-bar-striped': analysis.mapStatus === 'processing',
                  'bg-warning text-dark': analysis.mapStatus === 'uploading',
                  'bg-danger': analysis.mapStatus === 'failed'
                }">{{ analysis.mapStatus }}</span>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Agremo Analysis ID</label>
              <p class="mb-0"><code>{{ analysis.agremoAnalysisId || 'Not submitted' }}</code></p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Progress</label>
              <div class="d-flex align-items-center gap-2">
                <div class="progress flex-grow-1" style="height: 8px;">
                  <div
                    class="progress-bar"
                    [ngClass]="{
                      'bg-success': analysis.status === 'completed',
                      'bg-danger': analysis.status === 'failed',
                      'bg-info progress-bar-striped progress-bar-animated': analysis.status === 'processing' || analysis.status === 'downloading'
                    }"
                    [style.width.%]="analysis.progress || 0"
                  ></div>
                </div>
                <span class="small text-muted">{{ analysis.progress || 0 }}%</span>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div *ngIf="analysis.errorMessage" class="mt-3">
            <label class="form-label text-muted small mb-1">Error</label>
            <div class="alert alert-danger mb-0 py-2">
              <i class="bi bi-exclamation-triangle me-1"></i>
              {{ analysis.errorMessage }}
            </div>
          </div>
        </div>
      </div>

      <!-- Result Files -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-file-earmark-arrow-down me-2"></i>Result Files</h5>
        </div>
        <div class="card-body">
          <div *ngIf="!analysis.files || analysis.files.length === 0" class="text-muted text-center py-3">
            No result files available
          </div>

          <div *ngIf="analysis.files && analysis.files.length > 0" class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>File Type</th>
                  <th>Status</th>
                  <th>Downloaded</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let file of analysis.files">
                  <td>
                    <i [class]="getFileTypeIcon(file.fileType)" class="me-2"></i>
                    {{ getFileTypeLabel(file.fileType) }}
                  </td>
                  <td>
                    <span class="badge" [ngClass]="getDownloadStatusBadgeClass(file.downloadStatus)">
                      {{ file.downloadStatus }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(file.downloadedAt) }}</td>
                  <td>
                    <a
                      *ngIf="file.downloadUrl"
                      [href]="file.downloadUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="bi bi-download me-1"></i>
                      Download
                    </a>
                    <span *ngIf="!file.downloadUrl && file.downloadStatus === 'failed'" class="text-danger small">
                      <i class="bi bi-exclamation-triangle me-1"></i>
                      Download failed
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Statistics -->
      <div *ngIf="hasStatistics" class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Analysis Statistics</h5>
        </div>
        <div class="card-body">
          <app-agremo-statistics [statistics]="analysis!.statistics!"></app-agremo-statistics>
        </div>
      </div>
    </div>

    <!-- Right Column: Summary Sidebar -->
    <div class="col-lg-4">
      <div class="card sticky-top" style="top: 1rem;">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-clipboard-data me-2"></i>Summary</h5>
        </div>
        <div class="card-body">
          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Status</label>
            <div>
              <span class="badge" [ngClass]="getStatusBadgeClass(analysis.status)">
                {{ getStatusLabel(analysis.status) }}
              </span>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Organization</label>
            <p class="mb-0">{{ analysis.organizationName || '-' }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Field</label>
            <p class="mb-0">{{ analysis.fieldName || '-' }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Analysis Type</label>
            <p class="mb-0">{{ analysis.analysisTypeName || analysis.analysisTypeId || '-' }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Files</label>
            <p class="mb-0">
              <span class="text-success">{{ downloadedFiles.length }} downloaded</span>
              <span *ngIf="failedFiles.length > 0" class="text-danger ms-2">{{ failedFiles.length }} failed</span>
              <span *ngIf="pendingFiles.length > 0" class="text-warning ms-2">{{ pendingFiles.length }} pending</span>
            </p>
          </div>

          <div class="mb-3">
            <label class="form-label text-muted small mb-1">Internal ID</label>
            <p class="mb-0"><code class="small">{{ analysis.id }}</code></p>
          </div>

          <hr />

          <div class="d-grid gap-2">
            <button
              *ngIf="canRecheckMap()"
              class="btn btn-outline-warning btn-sm"
              [disabled]="recheckingMap"
              (click)="recheckMap()"
            >
              <span *ngIf="recheckingMap" class="spinner-border spinner-border-sm me-1"></span>
              <i *ngIf="!recheckingMap" class="bi bi-arrow-repeat me-1"></i>
              Recheck Map
            </button>
            <button
              *ngIf="canRetryDownload()"
              class="btn btn-outline-primary btn-sm"
              [disabled]="retryingDownload"
              (click)="retryDownload()"
            >
              <span *ngIf="retryingDownload" class="spinner-border spinner-border-sm me-1"></span>
              <i *ngIf="!retryingDownload" class="bi bi-arrow-clockwise me-1"></i>
              Retry Download
            </button>
            <button class="btn btn-outline-secondary btn-sm" (click)="goBack()">
              <i class="bi bi-arrow-left me-1"></i>
              Back to Analyses
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
